// Kartobjekt-registeret — ÉN identitet for alt som ligger i kartet.
//
// HVORFOR DENNE FILEN FINNES:
// UX-rapporten 2026-08-14 talte 21 klasser av artefakter i COP-kartet. 0 av dem
// sto i Objekter-lista, 2 kunne flyttes, 5 hadde ingen fjerne-vei i det hele
// tatt. Eieren hadde da meldt samme feilklasse TRE ganger — måleverktøyet
// (bølge 152), siktelinja og radiodekningen. Hver gang var svaret å legge ÉN
// ledning til, for ETT verktøy. Rapportens konklusjon:
//
//   «Å legge ledningen én gang til, for ett verktøy, vil produsere denne
//    rapporten på nytt om tre uker.»
//
// Registeret er svaret på det. Kjernegrepet er at EVNER ER DATA: et objekt
// erklærer selv om det kan velges, flyttes, redigeres, slettes og deles, og
// panelet rendrer nøyaktig det som er erklært — verken mer eller mindre. Da er
// eierens krav testbart, ikke bare implementert.
//
// ⚠ DETTE ER IKKE EN NY PARALLELL MODELL (grunnlov §3).
// Registeret LAGRER INGENTING. Et `Kartobjekt` er et HÅNDTAK som eieren av
// dataene lager på forespørsel — en projeksjon av `Track`, `PlacedUnit`, en
// terra-draw-figur, en `Teig` eller et analyseresultat, ikke en kopi av dem.
// Derfor kan de to ikke komme i utakt: det finnes bare én kopi av dataene, og
// registeret spør etter dem hver gang lista tegnes.
//
// ⚠ ÆRLIG KORRIGERING av min egen rapport (§6.1: «bygget på VOLVE-entiteten»):
// det er ikke gjort, og skal ikke gjøres. `@volve/kontrakter`-`Entitet` er
// merket server-sannhet som ved DESIGN ikke kan krysse wire, og `Persepsjon` er
// wire-formen av «hva én mottaker ser av ett objekt» i simuleringen. En
// siktelinje eller en radiodekningsflate er ingen av de to — den er en PÅSTAND
// OM TERRENGET med inndata, laget lokalt i klienten. Å presse den inn i
// persepsjonsmodellen ville vært å låne autoritet den ikke har. Det VOLVE-
// arbeidet faktisk låner hit, er PRINSIPPET (én identitet, evner som data),
// ikke typene.
//
// Ren logikk: ingen DOM, ingen MapLibre → hele kontrakten er testbar.

/** Klasser av objekter som kan ligge i kartet. Prefikset i `id` er typenavnet. */
export type KartobjektType =
  | 'spor'
  | 'enhet'
  | 'egenposisjon'
  | 'tegning'
  | 'teig'
  | 'maaling'
  | 'rute'
  | 'akse'
  | 'siktelinje'
  | 'dekning'
  | 'ild'
  | 'kapabilitet';

/** Hvor objektet kommer fra. Avgjør om sletting rammer andre enn meg. */
export type KartobjektKilde = 'egen' | 'delt' | 'sensor' | 'system';

/**
 * Hva operatøren kan gjøre med objektet — ERKLÆRT, ikke antatt.
 *
 * Alle fem feltene er påkrevd. Det er hele poenget: en ny type kan ikke «glemme»
 * å svare på om den kan slettes, for da kompilerer den ikke. `false` er et
 * lovlig og ærlig svar (en sensormåling skal ikke kunne flyttes), men det må
 * SIES.
 */
export interface Evner {
  /** Kan merkes: gir aktiv rad i lista, highlight i kartet og detaljpanel. */
  velgbar: boolean;
  /** Kan dras/flyttes i kartet. En MÅLING skal aldri være flyttbar (bølge 149). */
  flyttbar: boolean;
  /** Har en redigeringsflate (navn, parametre, geometri-håndtak). */
  redigerbar: boolean;
  /** Har en fjerne-vei. `false` betyr «hører til noe annet», ikke «glemt». */
  slettbar: boolean;
  /** Finnes hos andre operatører også → sletting er ikke min alene. */
  delbar: boolean;
}

/** Et objekt uten affordanser — grunnlinje for lesbare erklæringer. */
export const INGEN_EVNER: Readonly<Evner> = Object.freeze({
  velgbar: false, flyttbar: false, redigerbar: false, slettbar: false, delbar: false,
});

/**
 * En OBSERVASJON: kan velges og ses på, men aldri flyttes eller redigeres.
 *
 * Bindende presedens (bølge 149 + eierbeslutning 2026-08-14): «Soldater eller
 * posisjoner er jeg enig i» — en måling flyttet for hånd lyver om hvor den ble
 * gjort. Kilde og alder er udelelig fra verdien.
 */
export const OBSERVASJON: Readonly<Evner> = Object.freeze({
  velgbar: true, flyttbar: false, redigerbar: false, slettbar: false, delbar: false,
});

/**
 * Et PLANOBJEKT: operatørens eget, fullt manipulerbart.
 *
 * Eierbeslutning 2026-08-14: «et tegneverktøy må kunne slettes, flyttes og
 * redigeres … Så jeg godtar skillet.»
 */
export const PLANOBJEKT: Readonly<Evner> = Object.freeze({
  velgbar: true, flyttbar: true, redigerbar: true, slettbar: true, delbar: false,
});

/** Punktet lista og `flyTo` bruker. Linjer/flater oppgir sitt representative punkt. */
export interface Kartpunkt { lon: number; lat: number }

/**
 * Ett objekt i kartet, sett fra lista og detaljpanelet.
 *
 * Handlingene (`velg`, `flytt`, `rediger`, `slett`) implementeres av EIEREN av
 * dataene — registeret kaller dem bare. Kontrakten som `sjekkKartobjekt`
 * håndhever: er en evne erklært `true`, MÅ handlingen finnes.
 */
export interface Kartobjekt {
  /** Stabil id, prefikset med typen: `«tegning:td-14»`. Se `objektId`. */
  id: string;
  type: KartobjektType;
  kilde: KartobjektKilde;
  /** Navn til operatøren. Aldri en maskin-id når et menneskenavn finnes. */
  navn: string;
  /** Undertittel. For alt som er MÅLT skal alderen stå her (grunnloven). */
  undertittel: string;
  /** Skjulte søkeord (telefonnummer, Matrix-id) — søkbart uten å fylle raden. */
  sok?: string;
  /** APP-6-tilhørighet for prikkfargen. */
  aff?: string;
  /** Eksplisitt prikkfarge (overstyrer `aff`). */
  color?: string;
  /** Representativt punkt. Mangler den, kan objektet ikke `flyTo`-es. */
  senter?: Kartpunkt;
  /** Måletidspunkt/opprettelsestid (ms). */
  tsMs?: number;
  /** Hvor lenge visningen regnes som fersk (bølge 181 — VISNING, ikke levetid). */
  ferskhetMs?: number;
  /** Sant = skal ligge til noen aktivt slipper sporet (bølge 181 — EKSISTENS). */
  bevares?: boolean;
  /** Operatør eller ORBAT-node som eier objektet. */
  eier?: string;
  evner: Evner;
  /** Vis objektet: `flyTo` + detaljpanel. Påkrevd når `evner.velgbar`. */
  velg?: () => void;
  /** Start flytting (verktøyet tar over kartet). Påkrevd når `evner.flyttbar`. */
  flytt?: () => void;
  /** Åpne redigering. Påkrevd når `evner.redigerbar`. */
  rediger?: () => void;
  /** Fjern objektet. Påkrevd når `evner.slettbar`. */
  slett?: () => void;
}

/** Etikett per type — gruppeoverskriften i Objekter-lista. */
export const TYPE_ETIKETT: Readonly<Record<KartobjektType, string>> = Object.freeze({
  egenposisjon: 'Egen posisjon',
  spor: 'Spor',
  enhet: 'Enheter',
  tegning: 'Påtegninger',
  teig: 'Teiger',
  maaling: 'Målinger',
  rute: 'Ruter',
  akse: 'Fremrykningsakser',
  siktelinje: 'Siktelinjer',
  dekning: 'Radiodekning',
  ild: 'Ildstøtte',
  kapabilitet: 'Kapabiliteter',
});

/**
 * Rekkefølgen gruppene står i lista. Egen posisjon først: den er det ene
 * objektet operatøren alltid skal kunne finne uten å lete.
 */
export const TYPE_REKKEFOLGE: readonly KartobjektType[] = Object.freeze([
  'egenposisjon', 'spor', 'enhet', 'kapabilitet', 'tegning', 'teig',
  'siktelinje', 'dekning', 'ild', 'rute', 'akse', 'maaling',
] as const);

/** Bygg en prefikset id. Én funksjon, så prefikset ikke kan skrives feil. */
export function objektId(type: KartobjektType, raa: string): string {
  return `${type}:${raa}`;
}

/** Typen ut av en prefikset id, eller `null` om prefikset ikke er en kjent type. */
export function typeAv(id: string): KartobjektType | null {
  const t = id.slice(0, id.indexOf(':'));
  return (TYPE_REKKEFOLGE as readonly string[]).includes(t) ? (t as KartobjektType) : null;
}

/**
 * KONTRAKTSJEKKEN — skralla som gjør at feilklassen ikke kan komme tilbake.
 *
 * Returnerer en liste med brudd (tom = i orden). Kalles av registeret ved hvert
 * oppslag, og av testen for hver type vi har adoptert. Å erklære `slettbar:
 * true` uten å levere `slett` er nøyaktig feilen fra bølge 152 — en handling som
 * SER UT som veien ut, men ikke er det. Her blir den en kompileringsnær feil i
 * stedet for en operatørklage tre uker senere.
 */
export function sjekkKartobjekt(o: Kartobjekt): string[] {
  const brudd: string[] = [];
  if (!o.id.startsWith(`${o.type}:`)) {
    brudd.push(`id «${o.id}» mangler typeprefikset «${o.type}:»`);
  }
  if (!o.navn.trim()) brudd.push(`«${o.id}» har tomt navn`);
  if (o.evner.velgbar && !o.velg) brudd.push(`«${o.id}» er velgbar uten velg()`);
  if (o.evner.flyttbar && !o.flytt) brudd.push(`«${o.id}» er flyttbar uten flytt()`);
  if (o.evner.redigerbar && !o.rediger) brudd.push(`«${o.id}» er redigerbar uten rediger()`);
  if (o.evner.slettbar && !o.slett) brudd.push(`«${o.id}» er slettbar uten slett()`);
  // En MÅLING som kan flyttes er den alvorligste av bruddene: da kan en
  // GPS-posisjon dras med musa og fortsatt utgi seg for å være målt.
  if (o.evner.flyttbar && (o.type === 'spor' || o.type === 'egenposisjon')) {
    brudd.push(`«${o.id}» er en MÅLING og kan ikke være flyttbar (bølge 149)`);
  }
  return brudd;
}

/** En kilde svarer med sine objekter NÅ. Kalles hver gang lista tegnes. */
export type Objektkilde = () => Kartobjekt[];

/**
 * Registeret. Eier ingen data — bare hvem som kan spørres.
 *
 * `alle()` sorterer etter `TYPE_REKKEFOLGE`, så lista står i samme orden uansett
 * hvilken rekkefølge kildene ble registrert i.
 */
export class KartobjektRegister {
  private kilder = new Map<string, Objektkilde>();
  private lyttere: Array<() => void> = [];
  /** Kontraktsbrudd oppdaget ved siste `alle()`. Leses av utviklingsvarselet. */
  private _brudd: string[] = [];

  /**
   * Registrer en kilde. Dobbeltregistrering KASTER: to kilder med samme navn
   * betyr at noen har kopiert et oppsett, og da vil objektene dukke opp dobbelt
   * i lista uten at noen skjønner hvorfor.
   */
  registrer(navn: string, kilde: Objektkilde): void {
    if (this.kilder.has(navn)) {
      throw new Error(`KartobjektRegister: kilden «${navn}» er allerede registrert`);
    }
    this.kilder.set(navn, kilde);
  }

  /** Registrerte kildenavn — leses av skralletesten. */
  get kildenavn(): readonly string[] { return [...this.kilder.keys()]; }

  /** Alle objekter i kartet nå, gruppesortert. Kontraktsbrudd samles opp. */
  alle(): Kartobjekt[] {
    const ut: Kartobjekt[] = [];
    const brudd: string[] = [];
    for (const [navn, kilde] of this.kilder) {
      let deres: Kartobjekt[];
      try {
        deres = kilde();
      } catch (e) {
        // En kilde som kaster skal ikke kunne tømme HELE objektlista — da mister
        // operatøren oversikten over alt fordi ett verktøy har en feil.
        brudd.push(`kilden «${navn}» kastet: ${(e as Error).message}`);
        continue;
      }
      for (const o of deres) {
        const b = sjekkKartobjekt(o);
        if (b.length) brudd.push(...b.map((x) => `${navn}: ${x}`));
        ut.push(o);
      }
    }
    this._brudd = brudd;
    const rang = (t: KartobjektType) => {
      const i = TYPE_REKKEFOLGE.indexOf(t);
      return i === -1 ? TYPE_REKKEFOLGE.length : i;
    };
    return ut.sort((a, b) => rang(a.type) - rang(b.type) || a.navn.localeCompare(b.navn, 'nb'));
  }

  /** Kontraktsbrudd fra siste `alle()`. */
  get brudd(): readonly string[] { return this._brudd; }

  /** Slå opp ett objekt. Brukes av seleksjonen og av tastaturgestene. */
  finn(id: string): Kartobjekt | undefined {
    return this.alle().find((o) => o.id === id);
  }

  /** Meld at noe er endret → lista tegnes på nytt. */
  onEndring(fn: () => void): void { this.lyttere.push(fn); }

  /** Kall etter enhver endring i et verktøy som eier objekter. */
  meld(): void { for (const fn of this.lyttere) fn(); }
}

// ── Seleksjon — ÉN eier ─────────────────────────────────────────────────────

/** Hva som er merket nå. */
export interface Seleksjon { id: string; type: KartobjektType }

/**
 * Seleksjonseieren.
 *
 * HVORFOR: UX-rapporten fant FEM uavhengige «én om gangen»-mekanismer som ikke
 * visste om hverandre — highlight-markøren, kart-boblen, dropp-pinnen,
 * enhets-popupen og terra-draws seleksjon. De kunne peke på fem ulike objekter
 * samtidig, og lista viste aldri hvilken rad som var aktiv. Med én eier
 * DERIVERES kart-highlight, aktiv rad, detaljpanel og tastaturgester av samme
 * tilstand, og et kartklikk og et listeklikk havner samme sted.
 */
export class Seleksjonseier {
  private _valgt: Seleksjon | null = null;
  private lyttere: Array<(s: Seleksjon | null) => void> = [];

  get valgt(): Seleksjon | null { return this._valgt; }
  get valgtId(): string | null { return this._valgt?.id ?? null; }

  erValgt(id: string): boolean { return this._valgt?.id === id; }

  /**
   * Merk et objekt. Et objekt som ikke er `velgbar` kan ikke merkes — da er
   * `false` erklært, og lista skal ikke late som noe annet.
   */
  velg(o: Kartobjekt): boolean {
    if (!o.evner.velgbar) return false;
    if (this._valgt?.id === o.id) return true;
    this._valgt = { id: o.id, type: o.type };
    this.varsle();
    return true;
  }

  /** Opphev seleksjonen. Returnerer sant hvis noe faktisk ble opphevet. */
  opphev(): boolean {
    if (this._valgt === null) return false;
    this._valgt = null;
    this.varsle();
    return true;
  }

  /**
   * Rydd opp hvis det merkede objektet er borte (slettet, eller falt ut av
   * feeden). Kalles etter hver `alle()`. Uten dette kunne Delete peke på et
   * objekt som ikke finnes, og lista vise en aktiv rad som ikke er der.
   */
  behold(finnes: (id: string) => boolean): void {
    if (this._valgt && !finnes(this._valgt.id)) this.opphev();
  }

  onEndring(fn: (s: Seleksjon | null) => void): void { this.lyttere.push(fn); }

  private varsle() { for (const fn of this.lyttere) fn(this._valgt); }
}
